import { useMemo } from "react";

import {
  PortfolioAdditionalStatTile,
  PortfolioCardShell,
  PortfolioCardsList,
  PortfolioEmptyAdditionalStatTile,
  PortfolioMetricCard,
  PortfolioTrialHeader,
} from "@/views/Portfolio/components";
import type {
  PortfolioAdditionalStat,
  PortfolioMetricView,
  PortfolioSegment,
  PortfolioTrialHeaderInfo,
  TrialInfo,
} from "@/views/Portfolio/types";
import {
  buildPerSiteAdditionalStat,
  derivePortfolioMetricView,
  indexTrialsById,
  resolvePortfolioTrialHeader,
} from "@/views/Portfolio/utils";

type ComplianceBreakdownMetric = {
  value: number;
  delta_30d?: number | null;
  important_deviations_share: number;
  other_deviations_share: number;
  average_per_site?: number | null;
  standard_deviation_per_site?: number | null;
  range_per_site_low?: number | null;
  range_per_site_high?: number | null;
};

const COMPLIANCE_ADDITIONAL_STAT_UNIT = "PDs";

type TrialComplianceMetricsRow = {
  trial_id: string;
  protocol_deviations: ComplianceBreakdownMetric;
  open_protocol_deviations: ComplianceBreakdownMetric;
  closed_protocol_deviations: ComplianceBreakdownMetric;
};

type ComplianceMetricCard = {
  label: string;
  view: PortfolioMetricView;
  invertDelta: boolean;
  segments: PortfolioSegment[];
  additionalStat: PortfolioAdditionalStat;
};

type ComplianceCard = PortfolioTrialHeaderInfo & {
  metrics: [ComplianceMetricCard, ComplianceMetricCard, ComplianceMetricCard];
  initiallyExpanded: boolean;
};

const METRIC_COLORS = {
  protocol: {
    important: "#9AD7E9",
    others: "#0E98D0",
  },
  open: {
    important: "#F4C29B",
    others: "#DF7934",
  },
  closed: {
    important: "#BFE8D9",
    others: "#2E7D6B",
  },
} as const;

function ComplianceCardView({
  trialComplianceCard,
}: {
  trialComplianceCard: ComplianceCard;
}) {
  return (
    <PortfolioCardShell
      header={
        <PortfolioTrialHeader
          trial={trialComplianceCard}
          titleClassName="!font-bold"
          linkToTrial
        />
      }
      metricColumns={3}
      initiallyExpanded={trialComplianceCard.initiallyExpanded}
      additionalContent={trialComplianceCard.metrics.map((metricCard) =>
        metricCard.additionalStat ? (
          <PortfolioAdditionalStatTile
            key={`${trialComplianceCard.trial_id}-${metricCard.label}`}
            stat={metricCard.additionalStat}
            unit={COMPLIANCE_ADDITIONAL_STAT_UNIT}
          />
        ) : (
          <PortfolioEmptyAdditionalStatTile
            key={`${trialComplianceCard.trial_id}-${metricCard.label}`}
          />
        ),
      )}
    >
      {trialComplianceCard.metrics.map((metricCard) => (
        <PortfolioMetricCard
          key={`${trialComplianceCard.trial_id}-${metricCard.label}`}
          label={metricCard.label}
          view={metricCard.view}
          invertDelta={metricCard.invertDelta}
          segments={metricCard.segments}
        />
      ))}
    </PortfolioCardShell>
  );
}

function buildComplianceMetricCard(
  label: string,
  metricData: ComplianceBreakdownMetric,
  colors: {
    important: string;
    others: string;
  },
  options: {
    invertDelta: boolean;
    isComplete: boolean;
  },
): ComplianceMetricCard {
  const rawDelta = options.isComplete ? null : (metricData.delta_30d ?? null);

  return {
    label,
    view: derivePortfolioMetricView({
      actual: metricData.value,
      planned: { value: null, date: null, frequency: null },
      total: null,
      delta30d: rawDelta === null ? null : Math.round(rawDelta),
      lifecycle: options.isComplete ? "final" : "active",
      planTracked: false,
    }),
    invertDelta: options.invertDelta,
    segments: [
      {
        value: metricData.important_deviations_share,
        color: colors.important,
        label: "Important",
      },
      {
        value: metricData.other_deviations_share,
        color: colors.others,
        label: "Others",
      },
    ],
    additionalStat: buildPerSiteAdditionalStat(
      metricData.average_per_site,
      metricData.standard_deviation_per_site,
      metricData.range_per_site_low,
      metricData.range_per_site_high,
      1,
    ),
  };
}

function buildComplianceCard(
  trialMetricsRow: TrialComplianceMetricsRow,
  trialsById: Map<string, TrialInfo>,
): ComplianceCard {
  const trialInfo = trialsById.get(trialMetricsRow.trial_id);
  const trialHeader = resolvePortfolioTrialHeader(
    trialMetricsRow.trial_id,
    trialInfo,
  );

  return {
    ...trialHeader,
    metrics: [
      buildComplianceMetricCard(
        "PROTOCOL DEVIATIONS",
        trialMetricsRow.protocol_deviations,
        METRIC_COLORS.protocol,
        { invertDelta: true, isComplete: trialHeader.isComplete },
      ),
      buildComplianceMetricCard(
        "OPEN PROTOCOL DEVIATIONS",
        trialMetricsRow.open_protocol_deviations,
        METRIC_COLORS.open,
        { invertDelta: true, isComplete: trialHeader.isComplete },
      ),
      buildComplianceMetricCard(
        "CLOSED PROTOCOL DEVIATIONS",
        trialMetricsRow.closed_protocol_deviations,
        METRIC_COLORS.closed,
        { invertDelta: false, isComplete: trialHeader.isComplete },
      ),
    ],
    initiallyExpanded: false,
  };
}

export function PortfolioComplianceTrendsTab({
  data,
  trials,
}: {
  data?: TrialComplianceMetricsRow[];
  trials?: TrialInfo[];
}) {
  const cards = useMemo(() => {
    const complianceRows = Array.isArray(data) ? data : [];
    const trialsById = indexTrialsById(trials ?? []);

    return complianceRows.map((trialMetricsRow) =>
      buildComplianceCard(trialMetricsRow, trialsById),
    );
  }, [data, trials]);

  return (
    <PortfolioCardsList>
      {cards.map((trialComplianceCard) => (
        <ComplianceCardView
          key={trialComplianceCard.trial_id}
          trialComplianceCard={trialComplianceCard}
        />
      ))}
    </PortfolioCardsList>
  );
}
